import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  formatAppointmentDateTime,
  formatAppointmentSlotDuration,
  getAppointmentRowValue,
  normalizeAppointmentStatusLabel,
  renderAppointmentStatusPill,
} from "../eplannerReportUtils";

const INITIAL_BATCH = 25;
const SCROLL_BATCH = 25;

export function getAppointmentId(row) {
  return String(row?.AppointmentId ?? row?.appointmentId ?? "");
}

export function participantName(row, { hideName = false } = {}) {
  if (hideName) return "";
  const volName = getAppointmentRowValue(row, "VolName");
  if (volName) return String(volName).trim();
  const first = getAppointmentRowValue(row, "FirstName");
  const last = getAppointmentRowValue(row, "LastName");
  return [first, last].filter(Boolean).join(" ").trim() || "Participant";
}

function isLikelyGuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );
}

export function participantNumber(row) {
  const volunteerCandidates = [
    row?.VolId,
    row?.volId,
    row?.VolNumId,
    row?.volNumId,
    row?.VolunteerId,
    row?.volunteerId,
    row?.VolunteerNo,
    row?.volunteerNo,
  ];
  for (const candidate of volunteerCandidates) {
    if (candidate == null || String(candidate).trim() === "") continue;
    if (isLikelyGuid(candidate)) continue;
    return String(candidate).trim();
  }
  return "";
}

/** Display badge text, e.g. "VOL 4746". */
export function formatParticipantVolLabel(row) {
  const raw = participantNumber(row);
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (/^vol\b/i.test(trimmed)) {
    const code = trimmed.replace(/^vol\s*/i, "").trim();
    return code ? `VOL ${code}` : "VOL";
  }
  return `VOL ${trimmed}`;
}

/** Name for UI, or Vol label when hideName is on. */
export function participantDisplayLabel(row, { hideName = false } = {}) {
  if (hideName) {
    return formatParticipantVolLabel(row) || participantNumber(row) || "Participant";
  }
  return participantName(row) || formatParticipantVolLabel(row) || "Participant";
}

export function screeningOrRandomId(row) {
  const value = getAppointmentRowValue(row, "RandomId");
  if (value) return String(value);
  const screening = row?.ScreeningId ?? row?.screeningId;
  const random = row?.RdId ?? row?.rdId ?? row?.RandomId ?? row?.randomId;
  return String(screening || random || "").trim();
}

export function studyNumberLabel(row) {
  const value = getAppointmentRowValue(row, "StudyNumber");
  if (value == null || String(value).trim() === "") return "";
  return String(value).trim();
}

export function studyScheduleColor(row) {
  const color = getAppointmentRowValue(row, "StudySchColor");
  const text = String(color || "").trim();
  if (!text) return "#64748b";
  if (text.startsWith("#") || text.startsWith("rgb")) return text;
  if (/^[0-9a-fA-F]{6}$/.test(text)) return `#${text}`;
  if (/^[0-9a-fA-F]{3}$/.test(text)) return `#${text}`;
  return text;
}

function parseHexRgb(background) {
  const raw = String(background || "").replace("#", "").trim();
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const hex =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

/** Choose readable text color on a study badge background. */
export function contrastOnColor(background) {
  const rgb = parseHexRgb(background);
  if (!rgb) return "#ffffff";
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.62 ? "#0f172a" : "#ffffff";
}

/** Soft tint of a hex color for light badges (mix toward white). */
export function lightTintFromColor(background, mix = 0.82) {
  const rgb = parseHexRgb(background);
  if (!rgb) return "rgba(100, 116, 139, 0.14)";
  const amount = Math.min(1, Math.max(0, Number(mix) || 0.82));
  const r = Math.round(rgb.r + (255 - rgb.r) * amount);
  const g = Math.round(rgb.g + (255 - rgb.g) * amount);
  const b = Math.round(rgb.b + (255 - rgb.b) * amount);
  return `rgb(${r}, ${g}, ${b})`;
}

function AppointmentRow({ row, selected, onSelect, hideParticipantName = false }) {
  const name = hideParticipantName ? "" : participantName(row);
  const volNo = formatParticipantVolLabel(row) || participantNumber(row);
  const studyNo = studyNumberLabel(row);
  const studyColor = studyScheduleColor(row);
  const study = String(getAppointmentRowValue(row, "StudyName") ?? "—");
  const group = String(getAppointmentRowValue(row, "GroupName") ?? "—");
  const visit = String(getAppointmentRowValue(row, "VisitName") ?? "—");
  const when = formatAppointmentDateTime(getAppointmentRowValue(row, "StartTime"));
  const duration = formatAppointmentSlotDuration(
    getAppointmentRowValue(row, "SlotDuration"),
  );
  const status = getAppointmentRowValue(row, "AppointmentStatus");
  const statusLabel = normalizeAppointmentStatusLabel(status);
  const type = String(getAppointmentRowValue(row, "AppointmentType") ?? "").trim();
  const studyPath = [study, group, visit].filter(Boolean).join(" · ");
  const whenLine = [when || "—", duration && duration !== "—" ? duration : "", type]
    .filter(Boolean)
    .join(" · ");
  const hoverPreview = [
    studyNo ? `Study ${studyNo}` : "",
    studyPath,
    hideParticipantName ? volNo || name : volNo ? `${volNo} · ${name}` : name,
    whenLine,
    statusLabel && statusLabel !== "—" ? statusLabel : "",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        "eplanner-appt-row",
        selected ? "is-selected" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={hoverPreview}
      onClick={() => onSelect?.(row)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect?.(row);
        }
      }}
      aria-label={`${studyNo || study}, ${name}, ${visit}, ${statusLabel}, ${when}, ${duration}`}
    >
      <div className="eplanner-appt-row__top">
        <div className="eplanner-appt-row__line eplanner-appt-row__line--study">
          {studyNo ? (
            <span
              className="eplanner-appt-row__study-badge"
              style={{
                background: studyColor,
                color: contrastOnColor(studyColor),
              }}
            >
              {studyNo}
            </span>
          ) : (
            <span
              className="eplanner-appt-row__study-dot"
              style={{ background: studyColor }}
              aria-hidden="true"
            />
          )}
          <span className="eplanner-appt-row__path" title={studyPath}>
            <span className="eplanner-appt-row__study">{study}</span>
            <span className="eplanner-appt-row__sep">·</span>
            <span className="eplanner-appt-row__group">
              <i className="fas fa-users eplanner-appt-row__ico" aria-hidden="true" />
              {group}
            </span>
            <span className="eplanner-appt-row__sep">·</span>
            <span className="eplanner-appt-row__visit">
              <i className="fas fa-stethoscope eplanner-appt-row__ico" aria-hidden="true" />
              {visit}
            </span>
          </span>
        </div>
        <span className="eplanner-appt-row__status">
          {renderAppointmentStatusPill(status)}
        </span>
      </div>

      <div className="eplanner-appt-row__line eplanner-appt-row__line--volunteer">
        {volNo ? (
          <span className="eplanner-appt-row__vol-badge">{volNo}</span>
        ) : null}
        {!hideParticipantName && name ? (
          <span className="eplanner-appt-row__name" title={name}>
            {name}
          </span>
        ) : null}
        {hideParticipantName && !volNo ? (
          <span className="eplanner-appt-row__name">Participant</span>
        ) : null}
      </div>

      <div className="eplanner-appt-row__line eplanner-appt-row__line--when" title={whenLine}>
        <span className="eplanner-appt-row__when">{when || "—"}</span>
        {duration && duration !== "—" ? (
          <>
            <span className="eplanner-appt-row__sep">·</span>
            <span className="eplanner-appt-row__duration">{duration}</span>
          </>
        ) : null}
        {type ? (
          <>
            <span className="eplanner-appt-row__sep">·</span>
            <span className="eplanner-appt-row__type">{type}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function AppointmentCardList({
  rows = [],
  groups = null,
  loading = false,
  error = "",
  emptyHint = "",
  selectedRow = null,
  onSelect,
  hideParticipantName = false,
}) {
  const listRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH);
  const selectedId = getAppointmentId(selectedRow || {});
  const groupKey = Array.isArray(groups)
    ? groups.map((g) => `${g.key}:${g.rows?.length || 0}`).join("|")
    : "flat";

  useEffect(() => {
    setVisibleCount(INITIAL_BATCH);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [rows.length, groupKey]);

  const displayItems = useMemo(() => {
    if (!Array.isArray(groups) || !groups.length) {
      return rows.map((row, index) => ({
        type: "row",
        key: getAppointmentId(row) || `appt-row-${index}`,
        row,
      }));
    }
    return groups.flatMap((group) => [
      {
        type: "header",
        key: `group-${group.key}`,
        label: group.label,
        kind: group.kind || "",
        count: (group.rows || []).length,
      },
      ...(group.rows || []).map((row, index) => ({
        type: "row",
        key: getAppointmentId(row) || `appt-row-${group.key}-${index}`,
        row,
      })),
    ]);
  }, [groups, rows]);

  const visibleItems = useMemo(
    () => displayItems.slice(0, visibleCount),
    [displayItems, visibleCount],
  );

  const renderedRowCount = useMemo(
    () => visibleItems.filter((item) => item.type === "row").length,
    [visibleItems],
  );

  const hasMore = visibleCount < displayItems.length;

  const loadMore = useCallback(() => {
    setVisibleCount((current) =>
      Math.min(current + SCROLL_BATCH, displayItems.length),
    );
  }, [displayItems.length]);

  const handleScroll = useCallback(
    (event) => {
      if (!hasMore || loading) return;
      const target = event.currentTarget;
      const remaining =
        target.scrollHeight - target.scrollTop - target.clientHeight;
      if (remaining < 120) loadMore();
    },
    [hasMore, loading, loadMore],
  );

  return (
    <div
      className={[
        "eplanner-appointment-cards",
        loading ? "is-loading" : "",
        Array.isArray(groups) && groups.length ? "is-grouped" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-busy={loading}
    >
      <div
        ref={listRef}
        className="eplanner-appointment-cards__list"
        onScroll={handleScroll}
      >
        {error ? (
          <div className="eplanner-appointment-cards__empty eplanner-appointment-cards__empty--error">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <p className="mb-0">{error}</p>
          </div>
        ) : null}
        {loading && rows.length === 0 && !error ? (
          <div className="eplanner-appointment-cards__empty">Loading appointments…</div>
        ) : null}
        {!loading && !error && rows.length === 0 ? (
          <div className="eplanner-appointment-cards__empty">
            <i className="fas fa-calendar-day" aria-hidden="true" />
            <p className="mb-0">
              {emptyHint || "No appointments match the current filters."}
            </p>
          </div>
        ) : null}
        {visibleItems.map((item) =>
          item.type === "header" ? (
            <div key={item.key} className="eplanner-appt-group-head">
              <span className="eplanner-appt-group-head__label">{item.label}</span>
              <span className="eplanner-appt-group-head__count">{item.count}</span>
            </div>
          ) : (
            <AppointmentRow
              key={item.key}
              row={item.row}
              selected={getAppointmentId(item.row) === selectedId}
              onSelect={onSelect}
              hideParticipantName={hideParticipantName}
            />
          ),
        )}
        {hasMore ? (
          <div className="eplanner-appointment-cards__scroll-hint">
            Scroll for more · {renderedRowCount} of {rows.length}
          </div>
        ) : null}
      </div>

      {rows.length > 0 ? (
        <div className="eplanner-appointment-cards__footer">
          <div className="eplanner-appointment-cards__pager-meta">
            {renderedRowCount} of {rows.length}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function formatAppointmentListCount(count, loading = false) {
  if (loading) return "Loading…";
  return `${count} appointment${count === 1 ? "" : "s"}`;
}
